import * as Yup from "yup";
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Radio,
  RadioGroup,
  IconButton,
} from "@mui/material";
import { X, Save } from "lucide-react";
import DetailsSection from "../details/DetailsSection";
import LoanSummaryTable from "../details/LoanSummaryTable";
import EMIBreakdown from "../details/EMIBreakdown";
import { CustomAutocompleteSelect } from "../../../components/select";
import {
  OPERATIONS_TEAM_NAMES,
  BD_TEAM_NAMES,
  OPS_MANAGERS_NAMES,
} from "../constants/constants";
import { endpoints } from "../../../api/endpoint";
import SuccessErrorAlert from "../../../components/Alert/SuccessErrorAlert";
import { useAuth } from "../../../contexts/AuthContext";

const SHEET_ID = "1UkpVD4Nlunb6wuBZ-_gOhRYObJZ41TiwwoEYZWKMWIw";
const SHEET_API_KEY = "AIzaSyBtKOA5mvVP_zYyiQLkY8udrrTJaQStRig";

const driverIdValidator = (value) => {
  if (!value) return true;
  const regex = /^V-(2W|3W)-[A-Z]{3}-(?!0000)\d{4}$/;
  const regexTest = /^TEST-(2W|3W)-[A-Z]{3}-(?!00000)\d{4}$/;
  const regexForTrial = /^TRL-(2W|3W)-[A-Z]{3}-(?!00)\d{2,3}$/;
  return (
    regex.test(value) || regexForTrial.test(value) || regexTest.test(value)
  );
};

const opsSchema = Yup.object().shape({
  driverId: Yup.string()
    .required("Driver ID is required")
    .test("valid-driver-id", "Invalid Driver ID format", driverIdValidator),
  batteryId: Yup.string().required("Battery ID is required"),
  chargerId: Yup.string()
    .required("Charger ID is required")
    .min(2, "Charger ID is too short"),
  moveOrderNumber: Yup.string()
    .required("Move Order Number is required")
    .matches(/^MO-\d{5}$/, "Move Order Number must be in format MO-XXXXX"),
  harnessType: Yup.string().oneOf(
    ["short", "long"],
    "Please select a valid harness type",
  ),
  socMeterInstalled: Yup.boolean().default(false),
  referralSource: Yup.string().required("Referral source is required"),
  bdTeamMember: Yup.string().when("referralSource", {
    is: "bdTeam",
    then: (schema) => schema.required("BD Team member is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  operationsTeamMember: Yup.string().when("referralSource", {
    is: "operationTeam",
    then: (schema) => schema.required("Operations Team member is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  channelPartnerCode: Yup.string().when("referralSource", {
    is: "channelPartner",
    then: (schema) => schema.required("Channel Partner Code is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  referralAmount: Yup.number().when("referralSource", {
    is: "channelPartner",
    then: (schema) =>
      schema
        .required("Referral amount is required")
        .positive("Referral amount must be positive"),
    otherwise: (schema) => schema.notRequired(),
  }),
  formFilledBy: Yup.string().required("Form filled by is required"),
});

const submitOnboarding = async (payload) => {
  const response = await fetch(endpoints.createOnboardingDriver, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Submission failed");
  }

  return response.json();
};

const defaultFormValues = {
  driverId: "",
  batteryId: "",
  chargerId: "",
  moveOrderNumber: "",
  harnessType: "",
  socMeterInstalled: false,
  referralSource: "",
  bdTeamMember: "",
  operationsTeamMember: "",
  channelPartnerCode: "",
  referralAmount: null,
  formFilledBy: "",
};

const OnboardingFormDialog = ({ open, onClose, data: preOnboardingData }) => {
  const { user } = useAuth();
  const userEmail = user?.email || "Unknown User";

  const [allBatteryIds, setAllBatteryIds] = useState([]);
  const [allChannelPartnerCodes, setAllChannelPartnerCodes] = useState([]);
  const [allBDTeamNames, setAllBDTeamNames] = useState([]);
  const [allOperationsTeamNames, setAllOperationsTeamNames] = useState([]);
  const [allFormFilledByNames, setFormFilledByNames] = useState([]);

  const [formState, setFormState] = useState({
    submitError: null,
    isSubmitting: false,
    successMessage: "",
  });

  const updateFormState = (updates) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  const {
    watch,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(opsSchema),
    mode: "onBlur",
    defaultValues: defaultFormValues,
  });

  const referralSource = watch("referralSource");

  useEffect(() => {
    if (!open) return;

    reset(defaultFormValues);

    updateFormState({
      submitError: null,
      successMessage: "",
      isSubmitting: false,
    });
  }, [open, reset]);

  const driverDetails = preOnboardingData?.driverDetails || {};
  const vehicleFinancierDetails =
    preOnboardingData?.vehicleFinancierDetails || {};
  const loanSummary = preOnboardingData?.loanCalculationSummary || {};

  const onSubmit = async (formData) => {
    updateFormState({ submitError: null, isSubmitting: true });

    try {
      const payload = {
        prn: preOnboardingData.prn,
        driverDetails,
        loanSummary,
        financier: loanSummary?.financier,
        driverId: formData.driverId,
        batteryId: formData.batteryId,
        chargerId: formData.chargerId,
        moveOrderNumber: formData.moveOrderNumber,
        harnessType: formData?.harnessType || null,
        socMeterInstalled: formData.socMeterInstalled,
        referral: {
          source: formData.referralSource,
          bdTeamMember: formData.bdTeamMember || null,
          operationsTeamMember: formData.operationsTeamMember || null,
          channelPartnerCode: formData.channelPartnerCode || null,
          referralAmount: formData.referralAmount || null,
        },
        formFilledBy: formData.formFilledBy,
        createdBy: userEmail,
      };

      if (
        preOnboardingData?.vehicleFinanced === "Yes" &&
        vehicleFinancierDetails?.vehicleFinancierName
      ) {
        payload.vehicleFinancierDetails = vehicleFinancierDetails;
      }

      if (formData?.harnessType === "long") {
        payload.harness = "100AH Long Harness";
      } else {
        payload.harness = "100AH Short Harness";
      }

      const result = await submitOnboarding(payload);

      updateFormState({
        isSubmitting: false,
        successMessage: result.message || "Onboarding completed successfully",
      });

      setTimeout(() => {
        handleDialogClose();
      }, 2000);
    } catch (error) {
      updateFormState({
        submitError: error?.message || "Failed to submit form",
        isSubmitting: false,
      });
    }
  };

  const fetchDropdownData = async () => {
    const ranges = [
      "Complete Battery IDs!A:B",
      "Complete Channel Partner IDs!A:B",
      "BD Team Name!A:B",
      "Operations Team Name!A:B",
      "Form Filled By Name!A:B",
    ]
      .map(encodeURIComponent)
      .join("&ranges=");

    const res = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values:batchGet?ranges=${ranges}&key=${SHEET_API_KEY}`,
    );

    const data = await res.json();

    return data.valueRanges;
  };
  useEffect(() => {
    const loadData = async () => {
      const valueRanges = await fetchDropdownData();

      const batteryIds =
        valueRanges[0]?.values
          ?.slice(1)
          ?.filter((row) => row[1]?.trim().toUpperCase() === "DONE")
          ?.map((row) => row[0]?.trim().toUpperCase())
          ?.filter(Boolean)
          ?.sort() || [];

      const channelPartnerCodes =
        valueRanges[1]?.values
          ?.slice(1)
          ?.filter((row) => row[1]?.trim().toUpperCase() === "DONE")
          ?.map((row) => row[0]?.trim().toUpperCase())
          ?.filter(Boolean)
          ?.sort() || [];

      const bdTeamNames =
        valueRanges[2]?.values
          ?.slice(1)
          ?.filter((row) => row[1]?.trim().toUpperCase() === "DONE")
          ?.map((row) => row[0]?.trim())
          ?.filter(Boolean)
          ?.sort() || [];

      const operationsTeamNames =
        valueRanges[3]?.values
          ?.slice(1)
          ?.filter((row) => row[1]?.trim().toUpperCase() === "DONE")
          ?.map((row) => row[0]?.trim())
          ?.filter(Boolean)
          ?.sort() || [];

      const formFilledByNames =
        valueRanges[4]?.values
          ?.slice(1)
          ?.filter((row) => row[1]?.trim().toUpperCase() === "DONE")
          ?.map((row) => row[0]?.trim())
          ?.filter(Boolean)
          ?.sort() || [];

      setAllBatteryIds(batteryIds);
      setAllChannelPartnerCodes(channelPartnerCodes);
      setAllBDTeamNames(bdTeamNames);
      setAllOperationsTeamNames(operationsTeamNames);
      setFormFilledByNames(formFilledByNames);
    };

    loadData();
  }, []);

  const batteryIdsOptions = allBatteryIds.map((id) => ({
    value: id,
    label: id,
  }));

  const channelPartnerCodeOptions = allChannelPartnerCodes.map((id) => ({
    value: id,
    label: id,
  }));

  const operationTeamOptions = allOperationsTeamNames.map((id) => ({
    value: id,
    label: id,
  }));

  const bdTeamOptions = allBDTeamNames.map((id) => ({
    value: id,
    label: id,
  }));

  const formFilledByOptions = allFormFilledByNames.map((name) => ({
    value: name,
    label: name,
  }));

  const handleDialogClose = () => {
    updateFormState({
      successMessage: "",
      submitError: null,
      isSubmitting: false,
    });
    clearErrors();
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "300px", borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ position: "relative" }}>
        <Typography
          variant="h2"
          fontSize="1.5rem"
          sx={{ fontWeight: 600, color: "#1f2937" }}
        >
          Onboarding Form
        </Typography>
        <Typography variant="h4" fontSize="1.2rem" mt={0.5}>
          PRN: {preOnboardingData?.prn}
        </Typography>

        <IconButton
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            top: 5,
            right: 1,
          }}
        >
          <X />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <SuccessErrorAlert
          onErrorClose={() => updateFormState({ submitError: null })}
          onSuccessClose={handleDialogClose}
          successMessage={formState.successMessage}
          errorMessage={formState.submitError}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 1, color: "#1f2937" }}
            >
              Loan & EMI Details
            </Typography>

            {Object.keys(loanSummary).length > 0 && (
              <LoanSummaryTable loanData={loanSummary} sx={{ p: 0 }} />
            )}

            {loanSummary?.emi && (
              <EMIBreakdown emiData={loanSummary.emi} sx={{ p: 0 }} />
            )}
          </Box>

          <Divider />

          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 1, color: "#1f2937" }}
            >
              Driver & Vehicle Details
            </Typography>
            <DetailsSection
              title="Driver Details"
              data={driverDetails}
              sx={{ p: 0 }}
            />
            {preOnboardingData?.vehicleFinanced === "Yes" && (
              <>
                <Divider sx={{ my: 3 }} />
                <DetailsSection
                  title="Vehicle Financier Details"
                  data={vehicleFinancierDetails}
                  sx={{ p: 0 }}
                />
              </>
            )}
          </Box>

          <Divider />

          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 3, color: "#1f2937" }}
            >
              Referral Details
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth error={!!errors.referralSource}>
                <InputLabel>Referral Given By</InputLabel>
                <Controller
                  name="referralSource"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Referral Given By">
                      <MenuItem value="bdTeam">BD Team</MenuItem>
                      <MenuItem value="operationTeam">Operations Team</MenuItem>
                      <MenuItem value="channelPartner">
                        Channel Partner (CP)
                      </MenuItem>
                    </Select>
                  )}
                />
                {errors.referralSource && (
                  <FormHelperText>
                    {errors.referralSource.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            {referralSource === "bdTeam" && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth error={!!errors.bdTeamMember}>
                  <Controller
                    name="bdTeamMember"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <CustomAutocompleteSelect
                        label="BD Team Member"
                        placeholder="Select BD team member"
                        value={value || ""}
                        onChange={(option) => onChange(option || "")}
                        options={bdTeamOptions}
                        error={!!errors.bdTeamMember}
                        helperText={errors.bdTeamMember?.message}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            )}

            {referralSource === "operationTeam" && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth error={!!errors.operationsTeamMember}>
                  <Controller
                    name="operationsTeamMember"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <CustomAutocompleteSelect
                        label="Operations Team Member"
                        placeholder="Select a name"
                        value={value || ""}
                        onChange={(option) => onChange(option || "")}
                        options={operationTeamOptions}
                        error={!!errors.operationsTeamMember}
                        helperText={errors.operationsTeamMember?.message}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            )}

            {referralSource === "channelPartner" && (
              <Box>
                <FormControl
                  fullWidth
                  error={!!errors.channelPartnerCode}
                  sx={{ mb: 3 }}
                >
                  <Controller
                    name="channelPartnerCode"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <CustomAutocompleteSelect
                        label="Channel Partner Code"
                        placeholder="Select CP code"
                        value={value || ""}
                        onChange={(option) => onChange(option || "")}
                        options={channelPartnerCodeOptions}
                        error={!!errors.channelPartnerCode}
                        helperText={errors.channelPartnerCode?.message}
                      />
                    )}
                  />
                </FormControl>

                <Controller
                  name="referralAmount"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      label="Referral Amount (₹)"
                      type="number"
                      value={value || ""}
                      onChange={(e) =>
                        onChange(
                          e.target.value ? parseFloat(e.target.value) : "",
                        )
                      }
                      error={!!errors.referralAmount}
                      helperText={errors.referralAmount?.message}
                      placeholder="Enter referral amount"
                    />
                  )}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ mt: 3 }} />

          <Box sx={{ my: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 3, color: "#1f2937" }}
            >
              Asset Allocation
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: "20px",
                mb: 3,
              }}
            >
              <Controller
                name="driverId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value.toUpperCase())}
                    label="Driver ID"
                    placeholder="e.g., V-3W-IND-0001"
                    error={!!errors.driverId}
                    helperText={errors.driverId?.message}
                    inputProps={{
                      maxLength: 20,
                      style: { textTransform: "uppercase" },
                    }}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="batteryId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Box>
                    <CustomAutocompleteSelect
                      label="Battery ID"
                      placeholder="Select battery"
                      value={value || ""}
                      onChange={(option) => onChange(option || "")}
                      options={batteryIdsOptions}
                      error={!!errors.batteryId}
                      helperText={errors.batteryId?.message}
                    />
                    {/* <Button
                      size="small"
                      variant="text"
                      onClick={() => setBatteryManagerOpen(true)}
                      sx={{ mt: 0.5, color: "#059669", fontSize: "0.75rem" }}
                    >
                      + Manage Battery IDs
                    </Button> */}
                  </Box>
                )}
              />

              <Controller
                name="chargerId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value || ""}
                    onChange={onChange}
                    label="Charger ID"
                    placeholder="Enter charger ID"
                    error={!!errors.chargerId}
                    helperText={errors.chargerId?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="moveOrderNumber"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value.toUpperCase())}
                    label="Move Order Number (Zoho)"
                    placeholder="e.g., MO-00389"
                    error={!!errors.moveOrderNumber}
                    helperText={errors.moveOrderNumber?.message}
                    inputProps={{
                      maxLength: 8,
                      style: { textTransform: "uppercase" },
                    }}
                    fullWidth
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                p: 2,
                backgroundColor: "#f9fafb",
                borderRadius: "8px",
                mb: 3,
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Harness Selection
              </Typography>
              <Box sx={{ display: "flex", gap: 3 }}>
                <Controller
                  name="harnessType"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <RadioGroup
                      value={value || ""}
                      onChange={(e) => onChange(e.target.value)}
                      row
                    >
                      <FormControlLabel
                        value="short"
                        control={<Radio />}
                        label="100AH Short Harness"
                      />
                      <FormControlLabel
                        value="long"
                        control={<Radio />}
                        label="100AH Long Harness"
                      />
                    </RadioGroup>
                  )}
                />
              </Box>
              {errors.harnessType && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.harnessType.message}
                </FormHelperText>
              )}
            </Box>

            <Controller
              name="socMeterInstalled"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value || false}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label="100AH SOC Meter Installed"
                  />
                </Box>
              )}
            />

            <FormControl fullWidth error={!!errors.formFilledBy}>
              <Controller
                name="formFilledBy"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CustomAutocompleteSelect
                    label="Form Filled By"
                    placeholder="Select name"
                    value={value || ""}
                    onChange={(option) => onChange(option || "")}
                    options={formFilledByOptions}
                    error={!!errors.formFilledBy}
                    helperText={errors.formFilledBy?.message}
                  />
                )}
              />
            </FormControl>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {!formState.successMessage && (
          <>
            <Button
              variant="outlined"
              onClick={() => {
                reset();
                clearErrors();
              }}
              sx={{
                color: "#374151",
                borderColor: "#d1d5db",
                "&:hover": { backgroundColor: "#f3f4f6" },
              }}
            >
              Reset
            </Button>

            <Button
              onClick={handleDialogClose}
              disabled={formState.isSubmitting}
              variant="outlined"
              sx={{
                color: "#374151",
                borderColor: "#d1d5db",
                "&:hover": { backgroundColor: "#f3f4f6" },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              disabled={formState.isSubmitting}
              startIcon={
                formState.isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Save size={18} />
                )
              }
              sx={{
                backgroundColor: "#059669",
                "&:hover": { backgroundColor: "#047857" },
                "&:disabled": {
                  backgroundColor: "#d1d5db",
                  color: "#9ca3af",
                },
              }}
            >
              {formState.isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </>
        )}

        {formState.successMessage && (
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              color: "#374151",
              borderColor: "#d1d5db",
              "&:hover": { backgroundColor: "#f3f4f6" },
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingFormDialog;
